import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Trash2, UserCog, ShieldAlert, Wallet, LinkIcon, Copy, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, EmptyState, Loading } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ROLES = ["Manager", "Server", "Chef", "Bartender", "Host", "Cashier"];
const linkFor = (token) => `${window.location.origin}/invite/${token}`;

export default function StaffAccounts() {
  const [data, setData] = useState(null);
  const [invites, setInvites] = useState([]);
  const [role, setRole] = useState("Server");
  const [generating, setGenerating] = useState(false);
  const [lastLink, setLastLink] = useState(null);

  const load = useCallback(() => {
    api.get("/staff-accounts").then((r) => setData(r.data));
    api.get("/staff-invites").then((r) => setInvites(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Invite link copied");
    } catch {
      toast.message("Couldn't auto-copy — select and copy the link manually.");
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data: inv } = await api.post("/staff-invites", { staff_role: role });
      const link = linkFor(inv.token);
      setLastLink({ link, role });
      copy(link);
      load();
    } catch (err) { toast.error(apiErr(err)); }
    setGenerating(false);
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this pending invite?")) return;
    await api.delete(`/staff-invites/${id}`); toast.success("Invite revoked"); load();
  };

  const removeStaff = async (id) => {
    if (!window.confirm("Revoke this staff account?")) return;
    await api.delete(`/staff-accounts/${id}`); toast.success("Account revoked"); load();
  };

  const togglePayroll = async (s) => {
    const next = !s.can_view_payroll;
    try {
      await api.put(`/staff-accounts/${s.id}/permission`, { can_view_payroll: next });
      setData((d) => ({ ...d, staff: d.staff.map((x) => x.id === s.id ? { ...x, can_view_payroll: next } : x) }));
      toast.success(next ? "Salaries access granted" : "Salaries access revoked");
    } catch (err) { toast.error(apiErr(err)); }
  };

  if (!data) return <Loading />;
  const totalUsed = data.staff.length + invites.length;
  const atLimit = totalUsed >= data.limit;

  return (
    <div data-testid="staff-accounts-page">
      <PageHeader title="Staff Accounts" subtitle="Invite your team by link — they set their own password. Staff can log daily inventory and manage orders." />

      <div className="bg-card border border-border rounded-xl p-5 mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Seats used (staff + pending)</p>
          <p className="font-heading text-2xl font-semibold mt-1">{totalUsed} <span className="text-muted-foreground text-lg">/ {data.limit}</span></p>
        </div>
        <div className="w-40 h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary" style={{ width: `${Math.min(100, (totalUsed / data.limit) * 100)}%` }} />
        </div>
      </div>

      {/* Invite generator */}
      <div className="bg-card border border-border rounded-xl p-5 mb-6" data-testid="invite-generator">
        <div className="flex items-center gap-2 mb-3"><Send className="h-4 w-4 text-primary" /><h3 className="font-medium">Invite a team member</h3></div>
        {atLimit ? (
          <div className="flex items-start gap-3" data-testid="staff-limit-warning">
            <ShieldAlert className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">You've reached your plan's seat limit ({data.limit})</p>
              <p className="text-sm text-muted-foreground">Revoke a pending invite or upgrade to add more.</p>
              <Link to="/dashboard/billing" className="text-sm text-primary font-medium hover:underline mt-1 inline-block">View plans →</Link>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Role</p>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="w-44" data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="generate-invite-btn" onClick={generate} disabled={generating} className="bg-primary hover:bg-[#B35A42] text-primary-foreground">
              <LinkIcon className="h-4 w-4 mr-1" /> {generating ? "Generating..." : "Generate invite link"}
            </Button>
          </div>
        )}
        {lastLink && (
          <div className="mt-4 rounded-lg bg-muted/60 border border-border p-3" data-testid="generated-link">
            <p className="text-xs text-muted-foreground mb-1">Share this link with your {lastLink.role} (copied to clipboard):</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm break-all bg-background rounded px-2 py-1.5 border border-border">{lastLink.link}</code>
              <Button size="icon" variant="outline" onClick={() => copy(lastLink.link)} data-testid="copy-generated-link"><Copy className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Pending invites */}
      {invites.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Pending invites</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {invites.map((inv) => (
              <div key={inv.id} data-testid={`invite-${inv.id}`} className="bg-card border border-dashed border-border rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <Badge className="bg-amber-100 text-amber-700 border-0 hover:bg-amber-100"><Clock className="h-3 w-3 mr-1" /> Awaiting sign-up</Badge>
                  <button onClick={() => revoke(inv.id)} data-testid={`revoke-invite-${inv.id}`} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
                <p className="mt-2 font-medium">{inv.staff_role}</p>
                <Button size="sm" variant="outline" className="mt-2 w-full" onClick={() => copy(linkFor(inv.token))} data-testid={`copy-invite-${inv.id}`}>
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active staff */}
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Active staff</h3>
      {data.staff.length === 0 ? (
        <EmptyState icon={UserCog} title="No staff yet" desc="Generate an invite link above and share it with your team. They'll set their own password." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.staff.map((s) => (
            <div key={s.id} data-testid={`staff-account-${s.id}`} className="bg-card border border-border rounded-xl p-5 flex flex-col gap-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-full bg-secondary/15 text-secondary flex items-center justify-center font-heading font-semibold">{s.name.charAt(0)}</span>
                  <div>
                    <p className="font-medium">{s.name}</p>
                    <p className="text-sm text-muted-foreground">{s.email}</p>
                    <Badge className="mt-1 bg-muted text-muted-foreground border-0 hover:bg-muted">{s.staff_role}</Badge>
                  </div>
                </div>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeStaff(s.id)} data-testid={`delete-staff-account-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                <span className="flex items-center gap-2 text-sm text-foreground/80"><Wallet className="h-4 w-4 text-primary" /> Can view Salaries</span>
                <Switch checked={!!s.can_view_payroll} onCheckedChange={() => togglePayroll(s)} data-testid={`payroll-toggle-${s.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
